import {until} from 'lit-html/directives/until';
import '../wizard-steps';
import {LoadingEllipses} from '@epg/epg-components';
import '@modules/show-modal';
import {
  jobTypes,
  jobStatuses,
  pages,
  permissions,
  rosterErrors
} from '@modules/constants-enumerate';
import '../roster-summary-block';
import '../invalid-header-errors';
import '../invalid-column-count';
import '../invalid-row-errors';
import {hasOneOfPermissions} from '@modules/permissions';
import {generalLabels} from '@modules/i18n-labels';

export default (ctx, html) => html`
  ${renderResult(ctx, html)}
`;

const renderGroupSummaries = (ctx, html) => {
  const summaries = Object.keys(ctx.rosterJob.summary.groups)
    .map((level) => {
      if (parseInt(level, 10) <= parseInt(ctx.myLevel, 10)) return '';
      return html`
      <div class="col-lg-3 col-md-4 col-sm-6 col-xs-6">
        <roster-summary-block class="mb-5" .rosterSummary="${ctx.rosterJob.summary.groups[level]}">
          <span slot="label">${ctx.myGroups[level].label}</span>
        </roster-summary-block>
      </div>
    `;
    });

  return html`${summaries}`;
};

const renderResult = (ctx, html) => html`
  ${renderSummaries(ctx, html)}
  ${renderPendingState(ctx, html)}
  ${renderDoneState(ctx, html)}
  ${renderFailState(ctx, html)}
`;

const renderSummaries = (ctx, html) => {
  if (!ctx.rosterJob?.summary?.students || !ctx.isSameGroupId) {
    return;
  }

  const title = ctx.rosterJob.status === jobStatuses.PENDING
    ? ctx.__('<tt-KhY641gX>', 'Your uploaded roster will apply these changes:')
    : ctx.__('<tt-NROcnfYz>', 'Update summary:');

  const teachersLabel = ctx.__('<tt-RQd4H28b>', 'Teachers');

  return html`
    <h2 class="h4 font-weight-normal text-center">${until(title)}</h2>
    <div class="row  justify-content-start justify-content-md-center">
      ${renderSummary(ctx, html, 'students', ctx.__('<tt-x/v3eU2o>', 'Students'))}
      ${renderGroupSummaries(ctx, html)}
      ${renderSummary(ctx, html, 'classrooms', ctx.__('<tt-AEazC5vQ>', 'Classes'))}
      ${renderSummary(ctx, html, 'users', teachersLabel)}
      ${renderSummary(ctx, html, 'teachers', teachersLabel)}
      ${renderSummary(ctx, html, 'admins', generalLabels.administrators)}
    </div>
  `;
};

const renderSummary = (ctx, html, section, label) => {
  if (!ctx.rosterJob.summary[section]) {
    return;
  }

  return html`
    <div class="col-lg-3 col-md-4 col-sm-6 col-xs-6">
      <roster-summary-block class="mb-5" .rosterSummary="${ctx.rosterJob.summary[section]}">
        <span slot="label">${until(label)}</span>
      </roster-summary-block>
    </div>
  `;
};

const renderRosterInitiatedByHigherAdminMessage = (ctx,html) => {
  const level = parseInt(ctx.rosterJob.params_json.current_tier) - 2;
  if (level < 0) {
    return html`
      <div class="text-center font-weight-semibold font-size-lg">
        ${until(ctx.__('<tt-ZCmSd6m3>', 'A roster upload has already been initiated by a Top level Admin. This must be committed at the Top Level before any additional roster uploads can take place.'))}
      </div>`
  } else if (level > ctx.tierStructureLabels.length) {
    return html`
      <div class="text-center font-weight-semibold font-size-lg">
        ${until(ctx.__('<tt-DdL0QNAV>', 'A roster upload has already been initiated by a School Admin. This must be committed at the School Level before any additional roster uploads can take place.'))}
      </div>`
  } else {
    return html`
      <div class="text-center font-weight-semibold font-size-lg">
        ${until(ctx._r(ctx.__('<tt-6t/FBBy3>', 'A roster upload has already been initiated by a %r1% Admin. This must be committed at the %r1% Level before any additional roster uploads can take place.'), ctx.tierStructureLabels[level]))}
      </div>`
  }
}

const renderPendingState = (ctx, html) => {
  if (ctx.rosterJob.status !== jobStatuses.PENDING) {
    return;
  }

  if (!ctx.isSameGroupId) {
    return renderRosterInitiatedByHigherAdminMessage(ctx, html);
  }

  let message;
  let columnClass;
  let note;
  const actions = [];
  actions.push(html`
    <button class="btn btn-block btn-outline-primary rounded-pill" @click="${ctx.cancelJob}">${until(ctx.__('<tt-jCe+MACC>', 'Cancel Upload'))}</button>
  `);

  if (ctx.rosterJob.has_teacher_email_changes) {
    columnClass = 'col-xs-12 col-sm-4';
    message = ctx.__('<tt-5ti84pb6>', 'This roster contains email changes for one or more existing teachers. This action will update their login emails. They will be notified of this change, and passwords will remain unchanged. Proceed?');

    actions.push(html`
      <button class="btn btn-block btn-primary rounded-pill" @click="${ctx.commitRoster}">
        Save roster and new emails
      </button>
    `);

    actions.push(html`
      <button class="btn btn-block btn-primary rounded-pill" @click="${ctx.commitRosterNoEmailChange}">
        Save roster without changing emails
      </button>
    `);

    note = html`
      <p><strong>Note:</strong> It is recommended that teachers change their own emails, which they can do from their own profile pages.</p>
    `;
  } else {
    if (ctx.rosterJob.job_type === jobTypes.ROSTER_FULL) {
      columnClass = 'col-xs-6 col-sm-3';
      actions.push(html`
      <button class="btn btn-block btn-primary rounded-pill" @click="${ctx.commitRoster}">
        ${until(ctx.__('<tt-jl44S7FS>', 'Replace Roster'))}
      </button>
    `);
    }
    else {
      columnClass = 'col-xs-6 col-sm-3';
      message = ctx.__('<tt-YG61/TFT>', 'Do you want to commit the Roster you just uploaded?');
      actions.push(html`
      <button class="btn btn-block btn-primary rounded-pill" @click="${ctx.commitRoster}">
        ${until(ctx.rosterJob.skipped_count > 0 ? ctx._r(ctx.__('<tt-sd35xfWU>', 'Skip %r1% and Continue'), ctx.rosterJob.skipped_count) : ctx.__('<tt-iJfZ3T9Z>', 'Save Roster'))}
      </button>
    `);
    }
  }

  return html`
    <div class="d-flex flex-column  ${ctx.rosterJob.job_type === jobTypes.ROSTER_FULL ? '' : 'align-items-center text-center'}">
      ${ctx.rosterJob.job_type === jobTypes.ROSTER_FULL ? html`
        <h3 class="h4 font-weight-bold mt-3">
          ${until(ctx.__('<tt-GvDeELRz>', 'Confirm Replace Entire Roster'))}
        </h3>
        <h3 class="h4 font-weight-normal ${!ctx.replaceRosterConfirmationMatches ? 'mb-3': 'mb-5'}">
          ${until(ctx.__('<tt-cqzh54TH>', 'To avoid losing data, ensure your CSV file set has all roster information, including any updates. Data currently rostered that is not present in your CSV one will be archived, and any pre-existing records also present in the new roster will be updated accordingly. <br/><br/>Any changes you make from this point will affect existing accounts associated with this institution. <br/><br/>Type “'))}${until(ctx.__('<tt-nuwms1cu>', 'Replace Entire Roster'))}${until(ctx.__('<tt-S8pq4H4/>', '” in the box to continue: '))}
          <input name="replace_roster_confirmation" id="replace_roster_confirmation" @input="${ctx.updateReplaceRosterConfirmation}">
          ${!ctx.replaceRosterConfirmationMatches ? html`<p class="invalid-feedback offset-sm-3 d-block mt-3">${until(ctx.__('<tt-HTKbVRpK>', 'Please enter the correct confirmation text to continue.'))}</p>`: ''}

        </h3>` : html`<h3 class="h4 font-weight-normal">${until(message)}</h3>`}

      ${ctx.rosterJob.skipped_count > 0 ? html`
      <p class="font-weight-bold">
        ${until(ctx._r(ctx.__('<tt-lHQW8yQ7>', 'Your file contains %r1% new records you do not have permissions to create. To add them, please contact your admin. You still may proceed uploading your roster, but those records will be skipped.'), ctx.rosterJob.skipped_count))}
      </p>` : ''}
    </div>
    <div class="row justify-content-center align-items-center">
      ${actions.map((action) => html`<div class="${columnClass} mb-5">${action}</div>`)}
    </div>
    ${note}
  `;
};

const renderFailState = (ctx, html) => {
  if (ctx.rosterJob.status !== jobStatuses.FAILED) {
    return;
  }

  return html`
    <div class="row justify-content-center">
      <div class="col-xs-6 col-sm-4 mb-5">
        <link-to class="btn btn-block btn-primary rounded-pill" href="${pages.ROSTERS_ARCHIVE}">${until(ctx.__('<tt-n8fg8sxp>', 'Cancel'))}</button>
      </div>
    <div class="col-xs-6 col-sm-4 mb-5">
      <link-to class="btn btn-block btn-primary rounded-pill" href="${ctx.rosterUploadUrl}">${until(ctx.__('<tt-HtZBhYf6>', 'Reupload Roster'))}</link-to>
    </div>
  </div>
  `;
};

const renderDoneState = (ctx, html) => {
  if (ctx.rosterJob.status !== jobStatuses.DONE) {
    return;
  }

  const errors = ctx.rosterJob.errors || [];
  const productError = errors.filter((error) => error.type === rosterErrors.CONFLICTING_PRODUCT_EMAIL);

  const errorContent = productError.length === 0 ? '' : html`
    <p class="text-danger">
      ${until(ctx.__('<tt-tJv81P4B>', 'There was a problem changing one or more teacher emails in the Age of Learning products they have access to. Please contact your admin.'))}
      <button type="button" class="btn btn-link p-0" @click="${() => {
    ctx.showProductErrors = !ctx.showProductErrors;
  }}">${until(ctx.__('<tt-MgeUESST>', 'View conflicting emails'))}</button>
    </p>

    <ul class="${ctx.showProductErrors ? '' : 'd-none'}">
      ${productError[0].payload.map((email) => html`<li>${email}</li>`)}
    </ul>
  `;

  return html`
    <div class="row justify-content-center">
      <div class="col-xs-6 col-sm-4 mb-5">
        <button class="btn btn-block btn-link rounded-pill" @click="${ctx.download}">${until(ctx.__('<tt-pJVYTeZU>', 'Download CSV'))}</button>
      </div>
      <div class="col-xs-6 col-sm-4 mb-5">
        <span @click=${ctx.openLicenseModal} class="btn btn-block btn-primary rounded-pill ${ctx.rosterJob.status !== jobStatuses.DONE || !hasOneOfPermissions([permissions.LICENSE_APPLY]) ? 'disabled' : ''}" ?disabled="${ctx.rosterJob.status !== jobStatuses.DONE || !hasOneOfPermissions([permissions.LICENSE_APPLY])}">${until(ctx.__('<tt-N+tF2pwp>', 'Assign Licenses Now'))}</span>
      </div>
    </div>

    ${errorContent}
  `;
};
